/**
 * epcRedirect — PRODUCTDEV-6030. The EvedPay Cards experience moved to the v3 shell
 * ("EvedPay Cards (new)", /payments/evedpay-cards). Every OLD EPC link still points here:
 * email deep links (login?returnUrl=/d/…), the approval-widget URL constants, the
 * ApprovalDashboard server redirect, and internal $router.push calls. This module intercepts
 * those navigations at the router level and forwards the browser to the equivalent route in
 * the v3 shell, so no core (email builder) deploy is needed and links in already-sent emails
 * keep working.
 *
 * Deliberately NOT intercepted: plain /d/Dashboard/MyDashboard (without cardId / cardRefillId /
 * cardFundsId) — many non-EPC emails deep-link the dashboard.
 *
 * Mapping mirrors Eved.Frontend/src/clientPages/EvedPayCards/utils/legacyEpcRedirect.ts —
 * keep the two in sync.
 */

var NEW_BASE = '/payments/evedpay-cards'
var MAIN_DASHBOARD = '/dashboard/Dashboard/MainDashboard'

/** EvedPayCardAccounts.ID is a uniqueidentifier — account ids in links are GUIDs. */
var GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** decodeURIComponent that survives malformed input and (up to) double encoding. */
function safeDecode (value) {
  var out = value
  for (var i = 0; i < 2 && /%[0-9a-fA-F]{2}/.test(out); i++) {
    try {
      out = decodeURIComponent(out)
    } catch (e) {
      break
    }
  }
  return out
}

/** Parse `a=1&b=2` into a lowercase-keyed map (values keep their casing, e.g. CARD123). */
function parseParams (queryString) {
  var params = {}
  if (!queryString) return params
  var pairs = queryString.split('&')
  for (var i = 0; i < pairs.length; i++) {
    if (!pairs[i]) continue
    var eq = pairs[i].indexOf('=')
    var key = (eq >= 0 ? pairs[i].slice(0, eq) : pairs[i]).toLowerCase()
    var value = eq >= 0 ? pairs[i].slice(eq + 1) : ''
    if (key && !(key in params)) params[key] = value
  }
  return params
}

/** approve/decline from the email's Is{Approve|Reject}…FromEmail=true flags. */
function pickAction (params) {
  for (var key in params) {
    if (!params.hasOwnProperty(key)) continue
    if (String(params[key]).toLowerCase() !== 'true') continue
    if (/^isapprove\w*fromemail$/.test(key)) return 'approve'
    if (/^isreject\w*fromemail$/.test(key)) return 'decline'
  }
  return null
}

/**
 * Whether the new EPC tab's v3 routes exist for the acting user. The backend menu includes the
 * EPC module for admins/reconcile-close users, and for plain EvedPayCardUser holders ONLY when
 * they actually hold a card — verified live: EvedPayCardUser alone without a card gets no tab
 * (its routes would 404). When in doubt we return false: the v3 standalone envelope-approval page
 * and main dashboard work for everyone. Reads the user from the app instance, falling back to the
 * shared-origin localStorage (`eved-user` carries `user.HasEvedPayCard` when the v3 shell
 * populated it).
 */
export function hasEpcTabAccess () {
  var access = readUserAccess()
  if (!access.perms) return false
  function has (p) { return access.perms.indexOf(p) !== -1 }
  if (has('EvedPayCardAdministrator') || has('EvedPayCardViewReconcileClose')) return true
  return has('EvedPayCardUser') && access.hasCard
}

/**
 * PRODUCTDEV-9009: an ApprovalDashboard holder with no EvedPay Cards tab. Core confines them to the
 * Approval Dashboard, so their envelope links must stay on the navless envelope page.
 */
export function isApprovalDashboardRestricted () {
  var perms = readUserAccess().perms
  return !!perms && perms.indexOf('ApprovalDashboard') !== -1 && !hasEpcTabAccess()
}

/** The acting user's permissions (null when unknown) and HasEvedPayCard. */
function readUserAccess () {
  var perms = null
  var hasCard = false
  try {
    var vm = global.app && global.app.$user && global.app.$user.$
    perms = vm && vm.userPermissions
    var stored = JSON.parse(global.localStorage.getItem('eved-user') || '{}')
    if (!perms) perms = stored && stored.userPermissions
    hasCard = !!(stored && stored.user && stored.user.HasEvedPayCard)
  } catch (e) {
    perms = perms || null
  }
  return { perms: Array.isArray(perms) ? perms.map(String) : null, hasCard: hasCard }
}

/**
 * Envelope deep link. EPC users → the new envelope detail (id-agnostic `all` scope). Users
 * without EPC tab access → the v3 standalone envelope-approval page (PRODUCTDEV-6079; switched
 * over from the dashboard widget in PRODUCTDEV-9027). ApprovalDashboard users keep the dashboard
 * widget.
 */
function envelopeRoute (params, epcTabAccess, approvalDashboardRestricted) {
  var envId = (params['envelopeid'] || '').trim()
  if (!envId || !/^[\w-]+$/.test(envId)) return null
  var action = pickAction(params)
  if (approvalDashboardRestricted) {
    var dashQuery = 'approvalItem=' + encodeURIComponent('ENVELOPE' + envId)
    if (action) dashQuery += '&approvalAction=' + action
    return MAIN_DASHBOARD + '?' + dashQuery
  }
  var route = epcTabAccess
    ? NEW_BASE + '/all/envelopes/' + envId
    : '/payments/envelope-approval/' + envId
  if (action === 'approve') route += '?approve=1'
  if (action === 'decline') route += '?decline=1'
  return route
}

/**
 * Translate an old EPC route (`/d/…` / `/i/…` fullPath, possibly percent-encoded) into the
 * v3 hash-route string, or null when the URL is not an old-EPC target.
 */
export function translateEpcRoute (fullPath, options) {
  if (typeof fullPath !== 'string' || !fullPath) return null
  var epcTabAccess = !(options && options.epcTabAccess === false)
  var approvalDashboardRestricted = !!(options && options.approvalDashboardRestricted)

  var target = safeDecode(fullPath).trim()
  if (target.charAt(0) === '#') target = target.slice(1)
  if (target.charAt(0) !== '/') target = '/' + target

  var qIdx = target.indexOf('?')
  var pathPart = (qIdx >= 0 ? target.slice(0, qIdx) : target).replace(/\/+$/, '')
  var params = parseParams(qIdx >= 0 ? target.slice(qIdx + 1) : '')

  // `/i/…` ≡ `/d/…`; the iframe-era widget constant doubles the Programs segment.
  var norm = pathPart
    .replace(/^\/(d|i)\//i, '/d/')
    .replace(/^\/d\/Programs\/Programs\//i, '/d/Programs/')

  // Card / refill / funds approval emails → the v3 dashboard's approval widget (same backend
  // list, same prefixed item ids CARD{id} / REFILL{id} / FUNDS{id}).
  if (/^\/d\/Dashboard\/MyDashboard$/i.test(norm)) {
    var item = params['cardid'] || params['cardrefillid'] || params['cardfundsid']
    if (!item) return null // plain MyDashboard is not EPC's to redirect
    var action = pickAction(params)
    var query = 'approvalItem=' + encodeURIComponent(item)
    if (action) query += '&approvalAction=' + action
    return MAIN_DASHBOARD + '?' + query
  }

  if (/^\/d\/EnvelopeApprovalDashboard$/i.test(norm)) {
    if (approvalDashboardRestricted) return null
    return envelopeRoute(params, epcTabAccess) ||
      (epcTabAccess ? NEW_BASE + '/all/tables/envelopes' : MAIN_DASHBOARD)
  }

  var match = norm.match(/^\/d\/Programs\/>?Card(?:(-[a-z-]+)|\/([a-z-]+))?$/i)
  if (!match) return null
  var section = (match[1] ? match[1].slice(1) : match[2] || '').toLowerCase()

  // No EPC tab access → its v3 routes aren't registered (direct navigation would 404).
  // Envelope links go to the standalone approval page; everything else lands on the main dashboard.
  if (!epcTabAccess && section !== 'envelopes') {
    return MAIN_DASHBOARD
  }

  switch (section) {
    case 'list':
      var acct = (params['accountid'] || '').trim()
      // Verified: email AccountId == dbo.EvedPayCardAccounts.ID (uniqueidentifier) == Platform
      // account.id — a GUID. Unknown ids fall back to the tab root (shell canonicalizes).
      return GUID_RE.test(acct) ? NEW_BASE + '/' + acct : NEW_BASE
    case 'transactions':
      return NEW_BASE + '/all/tables/transactions'
    case 'history':
      return NEW_BASE + '/all/tables/history'
    case 'envelopes':
      return envelopeRoute(params, epcTabAccess, approvalDashboardRestricted) ||
        (epcTabAccess ? NEW_BASE + '/all/tables/envelopes' : MAIN_DASHBOARD)
    // '', cards, accounts, approval, account-statements and anything unknown → the tab root.
    default:
      return NEW_BASE
  }
}

/**
 * The v3 shell base URL for the CURRENT environment, derived from where this app is running
 * (verified against the core config transforms):
 *   https://portal.eved.com/{env}/index.html#/  →  https://portal.eved.com/{env}-v3/index.html#
 *   https://web.eved.com/#/          (prod)     →  https://web.eved.com/-v3/index.html#
 *   local (local.evedonline.eved.com / localhost) → https://local.evedonline.eved.com/dev-v3#
 * Returns null when the environment can't be recognized (then we do NOT redirect).
 */
export function getV3BaseUrl (loc) {
  var l = loc || global.location
  if (!l || !l.hostname) return null
  var host = l.hostname.toLowerCase()

  if (host === 'local.evedonline.eved.com' || host === 'localhost' || host === '127.0.0.1') {
    return 'https://local.evedonline.eved.com/dev-v3#'
  }
  if (host === 'web.eved.com') {
    return l.protocol + '//' + l.host + '/-v3/index.html#'
  }
  if (host === 'portal.eved.com') {
    // First path segment is the environment folder (e.g. /qainternaldemo/index.html).
    var seg = (l.pathname || '/').split('/').filter(function (s) { return !!s })[0]
    if (!seg) return null
    return l.protocol + '//' + l.host + '/' + seg + '-v3/index.html#'
  }
  return null
}

/**
 * Router guard: register BEFORE checkAuth (`router.beforeEach(epcRedirectGuard)`).
 * Forwards old-EPC navigations to the v3 shell — via window.top when this app is embedded in
 * the v3 LegacyPage iframe (same-origin in every deployed env; the iframe is not sandboxed).
 */
export function epcRedirectGuard (to, from, next) {
  var route = translateEpcRoute(to.fullPath, {
    epcTabAccess: hasEpcTabAccess(),
    approvalDashboardRestricted: isApprovalDashboardRestricted()
  })
  if (!route) return next()
  var base = getV3BaseUrl(global.location)
  if (!base) return next()

  var dest = base + route
  try {
    if (global.self !== global.top) {
      global.top.location.href = dest
    } else {
      global.location.href = dest
    }
  } catch (e) {
    global.location.href = dest
  }
  next(false)
}



// WEBPACK FOOTER //
// ./src/core/epcRedirect.js